import { Alert, BoxFooter, Button, Checkbox, Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { GREY_400, SPACING_0_5, SPACING_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { getCookie, removeCookie, setCookie } from '@mnd-frontend/utils';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import useCopyToClipboard from '../../../../../hooks/useCopyToClipboard';

const Info = styled.div`
  margin-top: ${SPACING_2};
  margin-bottom: ${SPACING_2};
`;

const Flex = styled.div`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${SPACING_1};
  gap: ${SPACING_1};

  p {
    margin-bottom: 0;
  }
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
`;

const Version = styled.p`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
`;

const SeparatorItem = styled.div`
  margin: ${SPACING_1} 0;
  padding: ${SPACING_0_5} 0;
  hr {
    border: 0;
    background: ${GREY_400};
    margin: 0;
    height: 1px;
  }
`;

const FeatureToggles = styled.div`
  margin-bottom: ${SPACING_2};
`;

const FeatureToggle = styled.p`
  margin-left: ${SPACING_1};
`;

type CookieOverridesProps = {
  onClose: () => void;
  viewAsAdmin: boolean;
};

const CookieOverrides: FC<CookieOverridesProps> = ({ onClose, viewAsAdmin }) => {
  const { showSuccessToast, showFailureToast } = useToasts();
  const [locale] = useState(getCookie('locale'));
  const [version] = useState(getCookie('frontend_version'));
  const [revokedAdminView] = useState(getCookie('revoked_admin_view'));
  const [featureTogglesObject] = useState<{ [key: string]: string }>(
    JSON.parse(getCookie('feature_toggles') ?? '{}'),
  );

  let featureToggles: [string, string][] = [];
  try {
    featureToggles = Object.entries(featureTogglesObject).sort(([a], [b]) => a.localeCompare(b));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  const url = version
    ? `${window.location.protocol}//${window.location.host}${window.location.pathname}?FRONTEND_VERSION=${version}`
    : '';
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [, copy] = useCopyToClipboard(url, inputRef);

  return (
    <>
      <div>
        <Text as="p" variant="body">
          This tab allows you to override the behaviour of the Publish tool. These overrides are
          only applied to you and will be persistent in this browser session until you clear them.
        </Text>
        <Flex>
          <Flex>
            <Checkbox
              label="View as admin"
              onChange={e => {
                setCookie('revoked_admin_view', String(!e.currentTarget.checked));
                window.location.reload();
              }}
              checked={viewAsAdmin}
            />
          </Flex>
          {revokedAdminView && (
            <Button
              type="button"
              configuration="toned"
              onClick={() => {
                removeCookie('revoked_admin_view');
                window.location.reload();
              }}
            >
              Clear
            </Button>
          )}
        </Flex>
        <Flex>
          <p>
            <b>Locale:</b> {typeof locale === 'string' ? locale : 'unset'}
          </p>
          {typeof locale === 'string' && (
            <Button
              type="button"
              configuration="toned"
              onClick={() => {
                removeCookie('locale');
                window.location.reload();
              }}
            >
              Clear
            </Button>
          )}
        </Flex>

        <Flex>
          <Version title={typeof version === 'string' ? version : undefined}>
            <b>Version:</b> {typeof version === 'string' ? version : 'unset'}
          </Version>
          {typeof version === 'string' && (
            <ButtonWrapper>
              <Button
                type="button"
                configuration="toned"
                onClick={() => {
                  removeCookie('frontend_version');
                  window.location.reload();
                }}
              >
                Clear
              </Button>
              <Button
                type="button"
                configuration="toned"
                onClick={() => {
                  try {
                    copy();
                    inputRef.current?.blur();

                    showSuccessToast('URL was successfully copied to clipboard');
                  } catch (_err) {
                    showFailureToast('Something failed while copying the URL to clipboard');
                  }
                }}
              >
                Copy URL
              </Button>
            </ButtonWrapper>
          )}
        </Flex>

        {featureToggles.length > 0 && (
          <>
            <SeparatorItem>
              <hr />
            </SeparatorItem>

            <FeatureToggles>
              <Text as="h4" variant="labelMedium">
                <b>Feature Toggles</b>
              </Text>
              {featureToggles.map(([key, value], index) => (
                <Flex key={index}>
                  <FeatureToggle>
                    <b>{key}:</b> {String(value)}
                  </FeatureToggle>
                  <Button
                    type="button"
                    configuration="toned"
                    onClick={() => {
                      const newFeatureTogglesObject = { ...featureTogglesObject };
                      delete newFeatureTogglesObject[key];
                      if (Object.keys(newFeatureTogglesObject).length === 0) {
                        removeCookie('feature_toggles');
                      } else {
                        setCookie('feature_toggles', newFeatureTogglesObject);
                      }

                      window.location.reload();
                    }}
                  >
                    Clear
                  </Button>
                </Flex>
              ))}
            </FeatureToggles>

            <Info>
              <Alert type="info">
                Removing specific Feature Toggles only work for toggles handled by Apollo Client.
              </Alert>
            </Info>
          </>
        )}

        {(locale !== undefined ||
          version !== undefined ||
          revokedAdminView !== undefined ||
          featureToggles.length > 0) && (
          <Info>
            <Alert
              type="warning"
              action={{
                text: 'Clear All Overrides',
                onClick: () => {
                  removeCookie('locale');
                  removeCookie('frontend_version');
                  removeCookie('revoked_admin_view');
                  removeCookie('feature_toggles');

                  window.location.reload();
                },
              }}
            >
              Cookie Overrides have been applied.
            </Alert>
          </Info>
        )}
      </div>
      <BoxFooter>
        <Button type="button" configuration="text" onClick={onClose}>
          Close
        </Button>
      </BoxFooter>

      <HiddenInput type="text" defaultValue={url} readOnly ref={inputRef} />
    </>
  );
};

export default CookieOverrides;
